import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { Container } from '@/components/container';
import { KeenIcon } from '@/components/keenicons';
import { useAbility } from '@/providers/';
import { useAuthContext } from '@/auth';
import { ConfirmationModal, DocumentPreviewModal, DocumentUploadModal } from '@/components/modal';
import { useUserOptions } from '@/hooks';
import demandLetterService from '@/services/letters/demandLetterService';
import letterTemplateService from '@/services/letters/letterTemplateService';
import eSignatureService from '@/services/e-signature/eSignatureService';
import { LetterEditor, MergeFieldPicker, fillInsIn } from '@/components/letter-editor';
import {
  ChecklistPanel, NegotiationModal, SendDemandModal,
  CHANNEL_SHORT, EVENT_LABELS, NEGOTIATION_KINDS, RESPONSE_LABELS, STATUS_BADGE, STATUS_LABELS, money,
} from '@/components/matter/demandLetters';

const ADDRESS_KEYS = ['address', 'address2', 'city', 'state', 'zip'];

/**
 * The demand letter page: body editor with fill-in chips, the terms and
 * recipient, readiness, approval, send, e-sign hand-off, negotiation log,
 * and the timeline.
 */
const DemandLetterEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { ability } = useAbility();
  const { currentUser } = useAuthContext();
  const editorRef = useRef(null);
  const loadUserOptions = useUserOptions({ defaultPageSize: 20 });
  const zoomPluginInstance = zoomPlugin();

  const canManage = ability.can('handle', 'demand-letter');
  const canSendPerm = ability.can('send', 'demand-letter');
  const canApprove = ability.can('approve', 'demand-letter');
  const canDelete = ability.can('delete', 'demand-letter');

  const [letter, setLetter] = useState(null);
  const [readiness, setReadiness] = useState({ missing: {}, fill_ins: [] });
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [states, setStates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [body, setBody] = useState('');
  const [details, setDetails] = useState({});
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const matterId = letter?.matter?.id;

  const [sendOpen, setSendOpen] = useState(false);
  const [negotiationOpen, setNegotiationOpen] = useState(false);
  const [esignOpen, setEsignOpen] = useState(false);
  const [esignFile, setEsignFile] = useState(null);
  const [esignLoading, setEsignLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind, title, message, btnText, onConfirm }
  const [note, setNote] = useState('');

  const applyPayload = (data) => {
    setLetter(data.letter);
    setBody(data.letter.body || '');
    setReadiness(data.readiness || { missing: {}, fill_ins: [] });
    setApprovalRequired(!!data.approval_required);
    setDetails({
      title: data.letter.title || '',
      attorney_id: data.letter.attorney?.id || '',
      recipient_name: data.letter.recipient_name || '',
      recipient_company: data.letter.recipient_company || '',
      recipient_email: data.letter.recipient_email || '',
      recipient_phone: data.letter.recipient_phone || '',
      recipient_fax: data.letter.recipient_fax || '',
      recipient_address: Object.fromEntries(ADDRESS_KEYS.map((k) => [k, data.letter.recipient_address?.[k] || ''])),
      demand_amount: data.letter.demand_amount || '',
      days_to_respond: data.letter.days_to_respond || 30,
      jurisdiction: data.letter.jurisdiction || '',
      email_subject: data.letter.email_subject || '',
    });
    setDirty(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, rules] = await Promise.all([demandLetterService.get(id), demandLetterService.rules()]);
      applyPayload(data);
      setStates(rules?.data?.states || []);
      const recordTypeId = data.letter.matter?.record_type?.id || null;
      letterTemplateService.mergeFields(recordTypeId).then((res) => setGroups(res?.data?.groups || [])).catch(() => {});
    } catch (error) {
      console.error('Error', error);
      toast.error('Could not load the demand letter.');
      navigate('/documents/demand-letters');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    loadUserOptions({ page: 1, pageSize: 100 }).then((r) => setUsers(r?.options || [])).catch(() => {});
  }, []);

  const locked = !!letter?.is_locked;
  const editable = canManage && !locked;
  const liveFillIns = useMemo(() => fillInsIn(body), [body]);
  const status = letter?.status;

  const updateDetail = (key, value) => {
    setDetails((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };
  const updateAddress = (key, value) => {
    setDetails((prev) => ({ ...prev, recipient_address: { ...prev.recipient_address, [key]: value } }));
    setDirty(true);
  };

  const run = async (label, fn, { reload = true } = {}) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res?.status !== false) {
        if (res?.data?.letter) applyPayload(res.data);
        else if (reload) await load();
        if (label) toast.success(label);
      }
      return res;
    } catch (error) {
      console.error('Error', error);
      const detail = error?.errors
        ? Object.entries(error.errors).filter(([k]) => k !== 'message').map(([, v]) => (Array.isArray(v) ? v.join(' ') : v)).join(' · ')
        : error?.message;
      toast.error('That did not work.', { description: detail || 'Please try again.' });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!editable) return true;
    const payload = { ...details, body, demand_amount: details.demand_amount || null, attorney_id: details.attorney_id || null, jurisdiction: details.jurisdiction || null };
    const res = await run('Saved.', () => demandLetterService.update(id, payload));
    return !!res;
  };

  const withSaved = async (fn) => {
    if (dirty || (editable && body !== letter?.body)) {
      const ok = await save();
      if (!ok) return;
    }
    await fn();
  };

  const previewPdf = () => withSaved(async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const blob = await demandLetterService.pdf(id);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (error) {
      console.error('Error', error);
      toast.error('Could not render the PDF.');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  });

  const downloadPdf = () => withSaved(async () => {
    try {
      const blob = await demandLetterService.pdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(letter?.title || 'demand-letter').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (error) {
      console.error('Error', error);
      toast.error('Could not download the PDF.');
    }
  });

  const openSend = () => withSaved(async () => setSendOpen(true));

  const handleSend = async (payload) => {
    const res = await run('Demand letter sent.', () => demandLetterService.send(id, payload));
    if (res) setSendOpen(false);
  };

  const openEsign = () => withSaved(async () => {
    setEsignLoading(true);
    try {
      const blob = await demandLetterService.pdf(id);
      const name = `${(letter?.title || 'demand-letter').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.pdf`;
      setEsignFile(new File([blob], name, { type: 'application/pdf' }));
      setEsignOpen(true);
    } catch (error) {
      console.error('Error', error);
      toast.error('Could not prepare the PDF for signature.');
    } finally {
      setEsignLoading(false);
    }
  });

  const handleEsignConfirm = async (values) => {
    const formData = new FormData();
    formData.append('coordinates', JSON.stringify(values.fields));
    formData.append('id', matterId);
    formData.append('type', 'matter');
    formData.append('email_subject', values.email_subject);
    formData.append('message', values.message || '');
    formData.append('demand_letter_id', id);
    if (values.due_date) formData.append('due_date', values.due_date);
    setEsignLoading(true);
    try {
      const { status: ok } = await eSignatureService.uploadDocForSignature(formData);
      if (ok) {
        toast.success('Sent for signature.');
        setEsignOpen(false);
        setEsignFile(null);
        await load();
      }
    } catch (error) {
      console.error('Error', error);
      toast.error('Could not send for signature.', { description: error?.data?.message || error?.message });
    } finally {
      setEsignLoading(false);
    }
  };

  const addNegotiation = async (payload) => {
    const res = await run('Entry logged.', () => demandLetterService.addNegotiation(id, payload));
    if (res) setNegotiationOpen(false);
  };

  const ask = (kind) => {
    const map = {
      regenerate: { title: 'Regenerate from the template?', message: 'The body is rebuilt from the template with the matter\'s current values. Your manual edits and filled-in prompts are lost.', btnText: 'Regenerate', className: 'btn-primary', action: () => demandLetterService.regenerate(id), label: 'Regenerated.' },
      void: { title: 'Void this letter?', message: 'A void letter stays on the record but cannot be sent or edited.', btnText: 'Void', className: 'btn-danger', action: () => demandLetterService.voidLetter(id), label: 'Letter voided.' },
      delete: { title: 'Delete this draft?', message: 'The draft is removed. This cannot be undone.', btnText: 'Delete', className: 'btn-danger', action: async () => { const r = await demandLetterService.remove(id); navigate(`/matters/detail/${matterId}?tab=Demand%20Letters`); return r; }, label: 'Draft deleted.' },
      revise: { title: 'Create a revision?', message: 'A new draft is created from this letter. The sent letter is kept as it went out.', btnText: 'Create revision', className: 'btn-primary', action: async () => { const r = await demandLetterService.revise(id); if (r?.data?.letter?.id) navigate(`/demand-letters/${r.data.letter.id}`); return r; }, label: 'Revision created.' },
      requestApproval: { title: 'Request approval?', message: 'Approvers are notified. The letter cannot be sent until one of them approves it.', btnText: 'Request', className: 'btn-primary', action: () => demandLetterService.requestApproval(id, { note: note || null }), label: 'Approval requested.', withNote: true },
      approve: { title: 'Approve this letter?', message: 'The drafter can send it as soon as you approve.', btnText: 'Approve', className: 'btn-success', action: () => demandLetterService.approve(id, { note: note || null }), label: 'Approved.', withNote: true },
      reject: { title: 'Return for changes?', message: 'The letter goes back to draft with your note.', btnText: 'Return', className: 'btn-warning', action: () => demandLetterService.rejectApproval(id, { note: note || null }), label: 'Returned to draft.', withNote: true },
    };
    setNote('');
    setConfirm({ kind, ...map[kind] });
  };

  const runConfirm = async () => {
    const c = confirm;
    setConfirm(null);
    if (c.kind === 'revise' || c.kind === 'delete') {
      await run(c.label, c.action, { reload: false });
      return;
    }
    await run(c.label, c.action);
  };

  if (loading || !letter) return <Container><div className="card p-6 text-sm text-gray-500">Loading demand letter…</div></Container>;

  const matterName = letter.matter?.name;
  const clientName = letter.matter?.contact ? [letter.matter.contact.first_name, letter.matter.contact.last_name].filter(Boolean).join(' ') : '';
  const missingList = Object.entries(readiness.missing || {});
  const esign = letter.esign_document;

  return (
    <Container>
      {/* Header */}
      <div className="card mb-4">
        <div className="card-header !block">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <button type="button" className="btn btn-sm btn-icon btn-light mt-0.5" onClick={() => navigate(`/matters/detail/${matterId}?tab=Demand%20Letters`)} title="Back to the matter">
                <KeenIcon icon="black-left" />
              </button>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="card-title">{letter.title}</h3>
                  <span className={`badge badge-pill badge-outline ${STATUS_BADGE[status] || 'badge-light'}`}>{STATUS_LABELS[status] || status}</span>
                  {letter.esign_status && <span className="badge badge-pill badge-outline badge-info">e-sign: {letter.esign_status.replace('_', ' ')}</span>}
                  {letter.supersedes && <span className="text-xs text-gray-500">revision of "{letter.supersedes.title}"</span>}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  <Link to={`/matters/detail/${matterId}`} className="link">{matterName}</Link>{clientName ? ` · ${clientName}` : ''}
                  {letter.sent_at ? ` · sent ${letter.sent_at}${letter.sent_by ? ` by ${letter.sent_by.first_name} ${letter.sent_by.last_name}` : ''}` : ''}
                  {letter.deadline_at ? ` · response due ${letter.deadline_at}` : ''}
                  {letter.sent_via?.length ? ` · via ${letter.sent_via.map((v) => CHANNEL_SHORT[v] || v).join(', ')}${letter.tracking_number ? ` (#${letter.tracking_number})` : ''}` : ''}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {editable && (
                <button type="button" className="btn btn-sm btn-light" disabled={busy || !dirty && body === letter.body} onClick={save}>
                  <KeenIcon icon="check" className="btn-icon" /> Save
                </button>
              )}
              <button type="button" className="btn btn-sm btn-light" disabled={busy || previewLoading} onClick={previewPdf}>
                <KeenIcon icon="eye" className="btn-icon" /> Preview PDF
              </button>
              <button type="button" className="btn btn-sm btn-light" disabled={busy} onClick={downloadPdf}>
                <KeenIcon icon="exit-down" className="btn-icon" /> Download
              </button>
              {editable && letter.template && (
                <button type="button" className="btn btn-sm btn-light" disabled={busy} onClick={() => ask('regenerate')} title="Rebuild from the template">
                  <KeenIcon icon="arrows-circle" className="btn-icon" /> Regenerate
                </button>
              )}
              {canManage && status !== 'void' && !['pending', 'partially_signed'].includes(letter.esign_status) && (
                <button type="button" className="btn btn-sm btn-light" disabled={busy || esignLoading} onClick={openEsign}>
                  <KeenIcon icon="notepad-edit" className="btn-icon" /> {esignLoading ? 'Preparing…' : 'Send for signature'}
                </button>
              )}
              {approvalRequired && canManage && status === 'draft' && (
                <button type="button" className="btn btn-sm btn-primary btn-outline" disabled={busy} onClick={() => withSaved(async () => ask('requestApproval'))}>
                  <KeenIcon icon="shield-tick" className="btn-icon" /> Request approval
                </button>
              )}
              {canApprove && status === 'pending_approval' && (
                <>
                  <button type="button" className="btn btn-sm btn-success" disabled={busy} onClick={() => ask('approve')}>
                    <KeenIcon icon="double-check" className="btn-icon" /> Approve
                  </button>
                  <button type="button" className="btn btn-sm btn-warning btn-outline" disabled={busy} onClick={() => ask('reject')}>Return for changes</button>
                </>
              )}
              {canSendPerm && !locked && (
                <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={openSend}>
                  <KeenIcon icon="sms" className="btn-icon" /> Send…
                </button>
              )}
              {canManage && letter.is_locked && status !== 'void' && (
                <button type="button" className="btn btn-sm btn-light" disabled={busy} onClick={() => ask('revise')}>
                  <KeenIcon icon="copy" className="btn-icon" /> Revise
                </button>
              )}
              {canManage && ['sent', 'responded'].includes(status) && (
                <button type="button" className="btn btn-sm btn-light" disabled={busy} onClick={() => setNegotiationOpen(true)}>
                  <KeenIcon icon="note-2" className="btn-icon" /> Log negotiation
                </button>
              )}
              {canDelete && status !== 'void' && (
                <button type="button" className="btn btn-sm btn-light text-red-600" disabled={busy} onClick={() => ask(locked ? 'void' : 'delete')}>
                  <KeenIcon icon="trash" className="btn-icon" /> {locked ? 'Void' : 'Delete'}
                </button>
              )}
            </div>
          </div>

          {locked && (
            <div className="mt-3 p-2.5 rounded-md bg-gray-100 text-xs text-gray-700">
              {status === 'void' ? 'This letter is void.' : `This letter was sent on ${letter.sent_at} and is locked. Create a revision to make changes.`}
            </div>
          )}
          {status === 'pending_approval' && (
            <div className="mt-3 p-2.5 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-900">
              Waiting for approval{letter.approval_requested_by ? ` — requested by ${letter.approval_requested_by.first_name} ${letter.approval_requested_by.last_name}` : ''}{letter.approval_notes ? `: "${letter.approval_notes}"` : ''}.
            </div>
          )}
          {status === 'approved' && (
            <div className="mt-3 p-2.5 rounded-md bg-green-50 border border-green-200 text-xs text-green-900">
              Approved{letter.approved_by ? ` by ${letter.approved_by.first_name} ${letter.approved_by.last_name}` : ''} on {letter.approved_at}. Editing the body or the terms will withdraw the approval.
            </div>
          )}
        </div>
      </div>

      <div className="grid xl:grid-cols-4 gap-4">
        {/* Body */}
        <div className="xl:col-span-3 space-y-4">
          {(liveFillIns.length > 0 || missingList.length > 0) && !locked && (
            <div className="p-3 rounded-md bg-yellow-50 border border-yellow-200 text-xs text-yellow-900 flex flex-wrap items-center justify-between gap-2">
              <div>
                {liveFillIns.length > 0 && <div><strong>{liveFillIns.length} prompt{liveFillIns.length === 1 ? '' : 's'} to write:</strong> click a highlighted prompt and type over it.</div>}
                {missingList.some(([token]) => !token.startsWith('letter.')) && (
                  <div>
                    <strong>Missing on the matter:</strong> {missingList.filter(([token]) => !token.startsWith('letter.')).map(([, label]) => label).join(', ')} —{' '}
                    <Link to={`/matters/detail/${matterId}?tab=About`} className="link">add them on the matter</Link>, or type over the chip.
                  </div>
                )}
                {missingList.some(([token]) => token.startsWith('letter.')) && (
                  <div>
                    <strong>Missing on this letter:</strong> {missingList.filter(([token]) => token.startsWith('letter.')).map(([, label]) => label).join(', ')} — fill in the Recipient &amp; terms panel, or type over the chip.
                  </div>
                )}
              </div>
              {liveFillIns.length > 0 && (
                <button type="button" className="btn btn-xs btn-light" onClick={() => editorRef.current?.nextFillIn()}>Next prompt</button>
              )}
            </div>
          )}
          <div className="card">
            <div className="card-body">
              <LetterEditor ref={editorRef} value={body} onChange={(html) => { setBody(html); setDirty(true); }} readOnly={!editable} mode="letter" minHeight={640} />
            </div>
          </div>
        </div>

        {/* Side panel */}
        <div className="space-y-4">
          <div className="card">
            <div className="card-header"><h3 className="card-title text-sm">Recipient &amp; terms</h3></div>
            <div className="card-body space-y-3 text-sm">
              <div>
                <label className="form-label mb-1 text-xs">Title</label>
                <input className="input input-sm" value={details.title} disabled={!editable} onChange={(e) => updateDetail('title', e.target.value)} />
              </div>
              <div>
                <label className="form-label mb-1 text-xs">Signing attorney</label>
                <select className="select select-sm" value={details.attorney_id} disabled={!editable} onChange={(e) => updateDetail('attorney_id', e.target.value)}>
                  <option value="">—</option>
                  {users.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="form-label mb-1 text-xs">Adjuster</label>
                  <input className="input input-sm" value={details.recipient_name} disabled={!editable} onChange={(e) => updateDetail('recipient_name', e.target.value)} />
                </div>
                <div>
                  <label className="form-label mb-1 text-xs">Carrier</label>
                  <input className="input input-sm" value={details.recipient_company} disabled={!editable} onChange={(e) => updateDetail('recipient_company', e.target.value)} />
                </div>
              </div>
              <div>
                <label className="form-label mb-1 text-xs">Email</label>
                <input className="input input-sm" value={details.recipient_email} disabled={!editable} onChange={(e) => updateDetail('recipient_email', e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="form-label mb-1 text-xs">Phone</label>
                  <input className="input input-sm" value={details.recipient_phone} disabled={!editable} onChange={(e) => updateDetail('recipient_phone', e.target.value)} />
                </div>
                <div>
                  <label className="form-label mb-1 text-xs">Fax</label>
                  <input className="input input-sm" value={details.recipient_fax} disabled={!editable} onChange={(e) => updateDetail('recipient_fax', e.target.value)} />
                </div>
              </div>
              <div>
                <label className="form-label mb-1 text-xs">Address</label>
                <input className="input input-sm mb-1" placeholder="Street" value={details.recipient_address?.address || ''} disabled={!editable} onChange={(e) => updateAddress('address', e.target.value)} />
                <input className="input input-sm mb-1" placeholder="Suite / line 2" value={details.recipient_address?.address2 || ''} disabled={!editable} onChange={(e) => updateAddress('address2', e.target.value)} />
                <div className="grid grid-cols-3 gap-1">
                  <input className="input input-sm" placeholder="City" value={details.recipient_address?.city || ''} disabled={!editable} onChange={(e) => updateAddress('city', e.target.value)} />
                  <input className="input input-sm" placeholder="ST" value={details.recipient_address?.state || ''} disabled={!editable} onChange={(e) => updateAddress('state', e.target.value)} />
                  <input className="input input-sm" placeholder="ZIP" value={details.recipient_address?.zip || ''} disabled={!editable} onChange={(e) => updateAddress('zip', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="form-label mb-1 text-xs">Demand amount</label>
                  <input className="input input-sm" type="number" min={0} step="0.01" value={details.demand_amount} disabled={!editable} onChange={(e) => updateDetail('demand_amount', e.target.value)} />
                </div>
                <div>
                  <label className="form-label mb-1 text-xs">Days to respond</label>
                  <input className="input input-sm" type="number" min={1} max={365} value={details.days_to_respond} disabled={!editable} onChange={(e) => updateDetail('days_to_respond', Number(e.target.value))} />
                </div>
              </div>
              <div>
                <label className="form-label mb-1 text-xs">Jurisdiction</label>
                <select className="select select-sm" value={details.jurisdiction} disabled={!editable} onChange={(e) => updateDetail('jurisdiction', e.target.value)}>
                  <option value="">None (no statutory checks)</option>
                  {states.map((s) => <option key={s.code} value={s.code}>{s.name}{s.statute ? ` (${s.statute})` : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label mb-1 text-xs">Email subject</label>
                <input className="input input-sm" value={details.email_subject} disabled={!editable} onChange={(e) => updateDetail('email_subject', e.target.value)} />
              </div>
            </div>
          </div>

          {!locked && (
            <MergeFieldPicker groups={groups} disabled={!editable} title="Insert a merge field" onInsert={(token, label) => editorRef.current?.insertToken(token, label)} />
          )}

          {(letter.negotiations || []).length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title text-sm">Negotiation</h3>
                {letter.response_status && (
                  <span className="badge badge-sm badge-outline badge-dark">{RESPONSE_LABELS[letter.response_status]}{letter.response_amount ? ` · ${money(letter.response_amount)}` : ''}</span>
                )}
              </div>
              <div className="card-body">
                <ul className="space-y-2">
                  {letter.negotiations.map((n) => (
                    <li key={n.id} className="text-xs border-b border-gray-100 pb-2 last:border-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-800">
                          {n.kind === 'demand' ? 'Demand' : (NEGOTIATION_KINDS.find((k) => k.value === n.kind)?.label || n.kind)} · {n.party === 'us' ? 'our office' : 'insurer'}
                          {n.amount ? ` · ${money(n.amount)}` : ''}
                        </span>
                        {canManage && n.kind !== 'demand' && (
                          <button type="button" className="text-red-600 hover:underline" onClick={() => run('Entry removed.', () => demandLetterService.removeNegotiation(id, n.id))}>remove</button>
                        )}
                      </div>
                      <div className="text-gray-500">{n.occurred_at}{n.expires_at ? ` · expires ${n.expires_at}` : ''}{n.created_by ? ` · ${n.created_by.first_name} ${n.created_by.last_name}` : ''}</div>
                      {n.notes && <div className="text-gray-700 mt-0.5 whitespace-pre-wrap">{n.notes}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {(letter.case_file || letter.signed_case_file || esign) && (
            <div className="card">
              <div className="card-header"><h3 className="card-title text-sm">Files &amp; signature</h3></div>
              <div className="card-body text-xs space-y-1.5">
                {letter.case_file && <div>Sent PDF filed as <Link to={`/matters/detail/${matterId}?tab=Files`} className="link">{letter.case_file.name}</Link></div>}
                {letter.signed_case_file && <div>Signed copy filed as <Link to={`/matters/detail/${matterId}?tab=Files`} className="link">{letter.signed_case_file.name}</Link></div>}
                {esign && (
                  <div>
                    e-signature {esign.status?.replace('_', ' ')}
                    {(esign.signers || []).length > 0 && (
                      <ul className="ml-3 mt-1 list-disc">
                        {esign.signers.map((s) => <li key={s.id}>{s.name || s.email} — {s.status}{s.signed_at ? ` (${s.signed_at})` : ''}</li>)}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header"><h3 className="card-title text-sm">Timeline</h3></div>
            <div className="card-body">
              <ul className="space-y-1.5">
                {(letter.events || []).map((e) => (
                  <li key={e.id} className="text-xs">
                    <span className="text-gray-800">{EVENT_LABELS[e.type] || e.type}</span>
                    <span className="text-gray-500"> · {e.occurred_at}{e.created_by ? ` · ${e.created_by.first_name} ${e.created_by.last_name}` : ''}</span>
                    {e.payload?.note && <div className="text-gray-600">“{e.payload.note}”</div>}
                    {e.payload?.reason && <div className="text-gray-600">{e.payload.reason}</div>}
                    {e.payload?.error && <div className="text-red-600">{e.payload.error}</div>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <SendDemandModal
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        letter={letter}
        readiness={{ ...readiness, fill_ins: liveFillIns }}
        approvalRequired={approvalRequired}
        onSend={handleSend}
        sending={busy}
      />

      <NegotiationModal open={negotiationOpen} onClose={() => setNegotiationOpen(false)} onSubmit={addNegotiation} loading={busy} />

      <DocumentPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fileUrl={previewUrl}
        loading={previewLoading}
        title="Demand letter preview"
        zoomPluginInstance={zoomPluginInstance}
      />

      {esignOpen && (
        <DocumentUploadModal
          show={esignOpen}
          onClose={() => { setEsignOpen(false); setEsignFile(null); }}
          onConfirm={handleEsignConfirm}
          Data={letter.matter}
          module="matter"
          title="Send demand letter for signature"
          loading={esignLoading}
          initialFile={esignFile}
        />
      )}

      {confirm && (
        <div className="modal-wrapper modal-animate-fade">
          <div className="modal-body modal-animate-scale w-4/5 max-w-xl">
            <button type="button" onClick={() => setConfirm(null)} className="modal-close-btn"><KeenIcon icon="cross" /></button>
            <h2 className="text-lg font-medium text-gray-900 my-2">{confirm.title}</h2>
            <p className="text-sm mb-3">{confirm.message}</p>
            {confirm.withNote && (
              <textarea className="input h-auto py-2 mb-3" rows={3} placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
            )}
            <div className="flex items-start justify-end gap-2">
              <button type="button" onClick={() => setConfirm(null)} className="btn btn-sm btn-light">Cancel</button>
              <button type="button" onClick={runConfirm} className={`btn btn-sm ${confirm.className}`} disabled={busy}>{confirm.btnText}</button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export { DemandLetterEditor };
